import { useSearchQuery } from "@/redux/services/search/search.api";
import { SearchResult } from "@/redux/services/search/search.type";
import {
  AlertCircle,
  BookOpen,
  LayoutList,
  Loader2,
  LucideIcon,
  Receipt,
  SearchX,
  SlidersHorizontal,
  UserRound,
  Users,
  UtensilsCrossed,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { BiSearch } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import OrderDetailsDrawer from "./order/OrderDetailsDrawer";
import {
  getDishPath,
  getOptionPath,
  getOptionValuePath,
  getSectionPath,
} from "@/lib/navigation";

const DEBOUNCE_MS = 300;

type ResultItem = {
  id: string;
  title: string;
  subtitle?: string;
  onSelect: () => void;
};

type ResultGroup = {
  key: keyof SearchResult;
  label: string;
  icon: LucideIcon;
  items: ResultItem[];
};

/** Emphasizes the first occurrence of the query inside a result label. */
const Highlight = ({ text, query }: { text: string; query: string }) => {
  const trimmed = query.trim();
  const index = trimmed
    ? text.toLowerCase().indexOf(trimmed.toLowerCase())
    : -1;

  if (index === -1) return <>{text}</>;

  return (
    <>
      {text.slice(0, index)}
      <mark className="bg-transparent font-semibold text-primary">
        {text.slice(index, index + trimmed.length)}
      </mark>
      {text.slice(index + trimmed.length)}
    </>
  );
};

const FormSearch = ({ placeholder }: { placeholder: string }) => {
  const inputId = useId();
  const navigate = useNavigate();
  const [query, setQuery] = useState<string>("");
  const [debouncedQuery, setDebouncedQuery] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const hasQuery = debouncedQuery.trim() !== "";
  const {
    data: results,
    isFetching,
    isError,
  } = useSearchQuery(debouncedQuery, { skip: !hasQuery });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const closeAnd = (action: () => void) => {
    setShowDropdown(false);
    action();
  };

  const groups = useMemo<ResultGroup[]>(() => {
    if (!results || !hasQuery) return [];

    const menuObjects = (
      key: keyof SearchResult,
      label: string,
      icon: LucideIcon,
      to: (item: {
        id: string;
        title: string;
        menu?: string;
        option?: string;
      }) => string
    ): ResultGroup => ({
      key,
      label,
      icon,
      items: (
        (results[key] ?? []) as {
          id: string;
          title: string;
          description?: string | null;
          menu?: string;
          option?: string;
        }[]
      ).map((item) => ({
        id: item.id,
        title: item.title,
        subtitle: item.description ?? undefined,
        onSelect: () => closeAnd(() => navigate(to(item))),
      })),
    });

    const built: ResultGroup[] = [
      {
        key: "orders",
        label: "Commandes",
        icon: Receipt,
        items: (results.orders ?? []).map((order) => ({
          id: order.id,
          title: `#${order.orderNumber}`,
          onSelect: () => closeAnd(() => setSelectedOrderId(order.id)),
        })),
      },
      {
        key: "customers",
        label: "Clients",
        icon: UserRound,
        items: (results.customers ?? []).map((customer) => ({
          id: customer.id,
          title: customer.fullName ?? customer.phone ?? "Client",
          subtitle: customer.phone ?? customer.email ?? undefined,
          // The detail route expects the global Customer id (customer.customer),
          // not the per-restaurant RestaurantCustomer id (customer.id).
          onSelect: () =>
            closeAnd(() => {
              if (customer.customer) navigate(`/customers/${customer.customer}`);
            }),
        })),
      },
      menuObjects("menus", "Menus", BookOpen, ({ id }) => `/menus/${id}`),
      menuObjects("menuSections", "Sections de menu", LayoutList, getSectionPath),
      menuObjects("products", "Plats", UtensilsCrossed, getDishPath),
      menuObjects("menuItemOptions", "Options", SlidersHorizontal, getOptionPath),
      menuObjects(
        "menuItemOptionValues",
        "Valeurs d'option",
        SlidersHorizontal,
        getOptionValuePath
      ),
      {
        key: "employees",
        label: "Employés",
        icon: Users,
        items: (results.employees ?? []).map((employee) => ({
          id: employee.id,
          title: employee.fullName ?? employee.phone,
          subtitle: employee.phone,
          onSelect: () => closeAnd(() => navigate("/settings")),
        })),
      },
    ];

    return built.filter((group) => group.items.length > 0);
  }, [results, hasQuery, navigate]);

  const flatItems = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  useEffect(() => {
    setActiveIndex(flatItems.length > 0 ? 0 : -1);
  }, [flatItems]);

  useEffect(() => {
    if (activeIndex < 0) return;
    listRef.current
      ?.querySelector(`[data-result-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setShowDropdown(false);
      return;
    }

    if (!showDropdown || flatItems.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % flatItems.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(
        (index) => (index - 1 + flatItems.length) % flatItems.length
      );
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      flatItems[activeIndex]?.onSelect();
    }
  };

  const renderDropdownContent = () => {
    if (!hasQuery || query.trim() === "") {
      return (
        <p className="p-4 text-center text-sm text-grayMedium">
          Recherchez une commande, un client, un menu, un plat...
        </p>
      );
    }

    if (isError) {
      return (
        <div className="flex items-center justify-center gap-2 p-4 text-sm text-red-500">
          <AlertCircle className="h-4 w-4 shrink-0" />
          La recherche a échoué. Réessayez.
        </div>
      );
    }

    if (isFetching && flatItems.length === 0) {
      return (
        <div className="flex items-center justify-center gap-2 p-4 text-sm text-grayMedium">
          <Loader2 className="h-4 w-4 animate-spin" />
          Recherche...
        </div>
      );
    }

    if (flatItems.length === 0) {
      return (
        <div className="flex flex-col items-center gap-1 p-4 text-center">
          <SearchX className="h-5 w-5 text-grayMedium" />
          <p className="text-sm font-medium text-dark">Aucun résultat</p>
          <p className="text-xs text-grayMedium">
            Aucun résultat pour « {debouncedQuery.trim()} »
          </p>
        </div>
      );
    }

    let flatIndex = -1;

    return groups.map((group) => (
      <div key={group.key} className="py-1">
        <h3 className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-grayMedium">
          <group.icon className="h-3.5 w-3.5" />
          {group.label}
        </h3>
        {group.items.map((item) => {
          flatIndex += 1;
          const index = flatIndex;

          return (
            <button
              key={item.id}
              type="button"
              data-result-index={index}
              className={`block w-full px-3 py-2 text-left text-sm transition-colors ${
                index === activeIndex ? "bg-gray-100" : "hover:bg-gray-50"
              }`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={item.onSelect}
            >
              <span className="block truncate text-dark">
                <Highlight text={item.title} query={debouncedQuery} />
              </span>
              {item.subtitle && (
                <span className="block truncate text-xs text-grayMedium">
                  <Highlight text={item.subtitle} query={debouncedQuery} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    ));
  };

  return (
    <div className="w-full relative" ref={containerRef}>
      <label htmlFor={inputId} className="relative block">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-grayDark">
          <BiSearch fontSize={20} />
        </span>
        <input
          type="search"
          placeholder={placeholder}
          className="bg-white block w-full py-3 pl-10 pr-10 rounded-md text-dark placeholder:text-grayMedium text-sm font-medium outline-none shadow-md transition-all duration-300 ease-in search__input border border-gray-200"
          id={inputId}
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        {isFetching && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-grayMedium">
            <Loader2 className="h-4 w-4 animate-spin" />
          </span>
        )}
      </label>

      {showDropdown && (
        <div
          ref={listRef}
          className="absolute z-50 left-0 right-0 mt-1 max-h-80 divide-y divide-gray-100 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
        >
          {renderDropdownContent()}
        </div>
      )}

      {selectedOrderId && (
        <OrderDetailsDrawer
          orderId={selectedOrderId}
          isOpen={true}
          onClose={() => setSelectedOrderId(null)}
        />
      )}
    </div>
  );
};

export default FormSearch;
